import { useEffect, useMemo, useState } from "react";
import { History, RefreshCw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  loadMyPqrHistory,
  loadMyPqrRequestDetails,
  loadPqrReplyThread,
} from "@/features/pqr/api/pqrClient";
import { createPqrAttachmentSignedLinks } from "@/features/pqr/api/pqrStorage";
import { MyRequestsList } from "@/features/pqr/components/MyRequestsList";
import { PQRRequestDetails } from "@/features/pqr/components/PQRRequestDetails";
import type {
  PqrAttachmentLink,
  PqrHistoryPage,
  PqrRequestDetails,
  PqrThreadMessage,
} from "@/features/pqr/pqr.types";

export function MyRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [historyStatus, setHistoryStatus] = useState<"idle" | "loading" | "error" | "ready">("idle");
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState<PqrHistoryPage | null>(null);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [detailsStatus, setDetailsStatus] = useState<"idle" | "loading" | "error" | "ready">("idle");
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [details, setDetails] = useState<PqrRequestDetails | null>(null);
  const [attachmentLinks, setAttachmentLinks] = useState<PqrAttachmentLink[]>([]);
  const [threadMessages, setThreadMessages] = useState<PqrThreadMessage[]>([]);

  useEffect(() => {
    let cancelled = false;
    setHistoryStatus("loading");
    setHistoryError(null);

    void loadMyPqrHistory(page)
      .then((result) => {
        if (cancelled) {
          return;
        }

        setHistoryPage(result);
        setHistoryStatus("ready");
        setSelectedRequestId((current) => {
          if (!result.items.length) {
            return null;
          }

          return result.items.some((item) => item.id === current) ? current : result.items[0].id;
        });
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setHistoryStatus("error");
        setHistoryError(t("pqr.errors.historyLoad"));
      });

    return () => {
      cancelled = true;
    };
  }, [page, refreshKey, t]);

  const filteredItems = useMemo(() => {
    const items = historyPage?.items ?? [];
    const query = searchQuery.trim().toLowerCase();

    if (!query) {
      return items;
    }

    return items.filter((item) =>
      item.subject.toLowerCase().includes(query)
      || item.ticketRef.toLowerCase().includes(query)
      || item.category.toLowerCase().replaceAll("_", " ").includes(query)
      || item.status.toLowerCase().replaceAll("_", " ").includes(query),
    );
  }, [historyPage?.items, searchQuery]);

  useEffect(() => {
    if (!filteredItems.length) {
      setSelectedRequestId(null);
      return;
    }

    setSelectedRequestId((current) =>
      filteredItems.some((item) => item.id === current) ? current : filteredItems[0].id
    );
  }, [filteredItems]);

  useEffect(() => {
    if (!selectedRequestId) {
      setDetails(null);
      setAttachmentLinks([]);
      setThreadMessages([]);
      setDetailsStatus("idle");
      setDetailsError(null);
      return;
    }

    let cancelled = false;
    setDetailsStatus("loading");
    setDetailsError(null);
    setThreadMessages([]);

    void Promise.all([
      loadMyPqrRequestDetails(selectedRequestId),
      loadPqrReplyThread(selectedRequestId),
    ])
      .then(async ([result, replies]) => {
        const links = result.attachmentPaths.length
          ? await createPqrAttachmentSignedLinks(result.attachmentPaths)
          : [];

        if (cancelled) {
          return;
        }

        setDetails(result);
        setAttachmentLinks(links);
        setThreadMessages(replies);
        setDetailsStatus("ready");
      })
      .catch(() => {
        if (cancelled) {
          return;
        }

        setDetailsStatus("error");
        setDetailsError(t("pqr.errors.detailLoad"));
      });

    return () => {
      cancelled = true;
    };
  }, [selectedRequestId, t]);

  const closeRequestsView = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/app");
  };

  return (
    <section className="mx-auto max-w-[106rem] overflow-hidden rounded-[2rem] border border-[#e1d7ca] bg-[#fffdf9] shadow-[0_32px_90px_-46px_rgba(34,26,19,0.42)]">
      <header className="flex items-start justify-between gap-6 border-b border-[#ece2d6] px-6 py-7 md:px-10 md:py-8">
        <div className="max-w-4xl space-y-3">
          <div className="inline-flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.24em] text-clay">
            <History className="size-4" />
            {t("pqr.myRequests.eyebrow")}
          </div>
          <div className="space-y-2">
            <h1 className="font-display text-[clamp(2.5rem,4.4vw,3.7rem)] leading-[0.94] text-ink">
              {t("pqr.myRequests.title")}
            </h1>
            <p className="max-w-4xl text-base leading-7 text-ink/65">
              {t("pqr.myRequests.subtitle")}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            className="h-12 w-12 rounded-full border border-[#e6dacb] bg-white p-0 text-ink shadow-none hover:bg-[#fffaf4]"
            onClick={() => setRefreshKey((current) => current + 1)}
            type="button"
            variant="outline"
          >
            <RefreshCw className="size-5" />
            <span className="sr-only">{t("pqr.actions.refresh")}</span>
          </Button>
          <button
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="inline-flex size-12 items-center justify-center rounded-full text-ink/55 transition hover:bg-[#f5efe7] hover:text-ink"
            onClick={closeRequestsView}
            type="button"
          >
            <X className="size-7" />
          </button>
        </div>
      </header>

      {historyStatus === "error" && !historyPage ? (
        <div className="border-b border-[#ece2d6] px-6 py-4 text-sm text-clay md:px-10">
          {historyError}
        </div>
      ) : null}

      {historyStatus === "loading" && !historyPage ? (
        <div className="border-b border-[#ece2d6] px-6 py-4 text-sm text-ink/65 md:px-10">
          {t("pqr.myRequests.loading")}
        </div>
      ) : null}

      <div className="grid min-h-[calc(100vh-15rem)] xl:grid-cols-[33.5rem_minmax(0,1fr)]">
        <MyRequestsList
          activeRequestId={selectedRequestId}
          isLoading={historyStatus === "loading"}
          items={filteredItems}
          page={historyPage?.page ?? page}
          pageCount={historyPage?.pageCount ?? 1}
          searchQuery={searchQuery}
          totalCount={historyPage?.totalCount ?? 0}
          onOpenRequest={setSelectedRequestId}
          onPageChange={setPage}
          onSearchChange={setSearchQuery}
        />

        <PQRRequestDetails
          attachmentLinks={attachmentLinks}
          details={details}
          emptyBody={searchQuery
            ? t("pqr.myRequests.searchEmptyBody")
            : t("pqr.myRequests.emptySelection")}
          errorMessage={detailsError}
          isLoading={detailsStatus === "loading"}
          threadMessages={threadMessages}
          title={t("pqr.myRequests.detailTitle")}
          variant="sheet"
        />
      </div>
    </section>
  );
}
